import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtPct, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Plus, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, Trash2, Loader2, Info, Pencil,
} from "lucide-react";

const OBJECTIVES = ["Awareness", "Traffic", "Engagement", "Lead Generation", "App Promotion", "Sales / Conversions"];
const STATUSES = ["Draft", "Active", "Paused", "Completed"];
const MARKETS = ["Global", "United States", "United Kingdom", "Germany", "France", "Canada", "Australia", "UAE", "Japan"];

const CAMP_RE = /^KARYOW_[A-Z]+_[A-Z0-9]+_[A-Z0-9]+/;
const AS_RE = /^AS_[A-Z0-9]+_[A-Z0-9]+/;
const AD_RE = /^AD[0-9]{2}_[A-Z0-9]+/;
const FORMATS = ["Image", "Video", "Carousel", "Collection"];

const statusStyle = (s) => ({
  Active: "bg-lime-400/15 text-lime-400 border-lime-400/30",
  Paused: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  Draft: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  Completed: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
}[s] || "bg-zinc-500/15 text-zinc-400");

function NamingBadge({ name, re = CAMP_RE }) {
  const ok = re.test(name);
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${ok ? "text-lime-400 border-lime-400/30 bg-lime-400/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {ok ? "Naming OK" : "Check naming"}
    </span>
  );
}

export default function Campaigns() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [data, setData] = useState({ campaigns: [], adsets: [], ads: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "KARYOW_AWR_GLOBAL_2026Q4", objective: "Awareness", market: "Global",
    audience_notes: "", daily_budget: 50, start_date: new Date().toISOString().slice(0, 10), status: "Draft",
  });
  const [asDialog, setAsDialog] = useState({ open: false, campaign_id: null, editId: null });
  const [asForm, setAsForm] = useState({ name: "AS_BROAD_MARKET01_18PLUS", audience_notes: "", daily_budget: 25, status: "Draft" });
  const [adDialog, setAdDialog] = useState({ open: false, campaign_id: null, adset_id: null, editId: null });
  const [adForm, setAdForm] = useState({ name: "AD01_GLOBAL_MARKETPLACE_VIDEO", format: "Video", status: "Draft", creative_id: "" });
  const [creatives, setCreatives] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/campaigns");
      setData(res.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/ai/creatives").then((r) => setCreatives(r.data.creatives || [])).catch(() => {});
  }, []);

  const creativeById = (id) => creatives.find((c) => c.id === id);

  const attachCreative = (id) => {
    if (id === "none") { setAdForm((f) => ({ ...f, creative_id: "" })); return; }
    const c = creativeById(id);
    if (!c) return;
    const content = c.content || {};
    const hasVideo = !!content.video_script?.hook;
    const headline = (content.headlines || [])[0] || c.title || "AD";
    const slug = headline.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 28);
    setAdForm((f) => ({
      ...f,
      creative_id: id,
      format: hasVideo ? "Video" : "Image",
      name: f.name && adDialog.editId ? f.name : `AD01_${slug || "CREATIVE"}`,
    }));
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Campaign name required"); return; }
    setSaving(true);
    try {
      await api.post("/campaigns", { ...form, daily_budget: Number(form.daily_budget) });
      toast.success("Draft campaign created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Failed to create campaign");
    } finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/campaigns/${id}`, { status });
    toast.success(`Status → ${status}`);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/campaigns/${id}`);
    toast.success("Campaign deleted");
    load();
  };

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const openAdSet = (campaign_id) => {
    setAsForm({ name: "AS_BROAD_MARKET01_18PLUS", audience_notes: "", daily_budget: 25, status: "Draft" });
    setAsDialog({ open: true, campaign_id, editId: null });
  };
  const openEditAdSet = (s) => {
    setAsForm({ name: s.name, audience_notes: s.audience_notes || "", daily_budget: s.daily_budget || 0, status: s.status });
    setAsDialog({ open: true, campaign_id: s.campaign_id, editId: s.id });
  };
  const createAdSet = async () => {
    if (!asForm.name.trim()) { toast.error("Ad set name required"); return; }
    setSaving(true);
    try {
      const body = { ...asForm, daily_budget: Number(asForm.daily_budget) };
      if (asDialog.editId) {
        await api.put(`/adsets/${asDialog.editId}`, body);
        toast.success("Ad set updated");
      } else {
        await api.post("/adsets", { campaign_id: asDialog.campaign_id, ...body });
        toast.success("Ad set created");
      }
      setExpanded((p) => ({ ...p, [asDialog.campaign_id]: true }));
      setAsDialog({ open: false, campaign_id: null, editId: null });
      load();
    } catch (e) { toast.error("Failed to save ad set"); }
    finally { setSaving(false); }
  };
  const deleteAdSet = async (id) => {
    await api.delete(`/adsets/${id}`);
    toast.success("Ad set deleted");
    load();
  };

  const openAd = (campaign_id, adset_id) => {
    setAdForm({ name: "AD01_GLOBAL_MARKETPLACE_VIDEO", format: "Video", status: "Draft", creative_id: "" });
    setAdDialog({ open: true, campaign_id, adset_id, editId: null });
  };
  const openEditAd = (a) => {
    setAdForm({ name: a.name, format: a.format || "Image", status: a.status, creative_id: a.creative_id || "" });
    setAdDialog({ open: true, campaign_id: a.campaign_id, adset_id: a.adset_id, editId: a.id });
  };
  const createAd = async () => {
    if (!adForm.name.trim()) { toast.error("Ad name required"); return; }
    setSaving(true);
    try {
      const body = { ...adForm, creative_id: adForm.creative_id || (adDialog.editId ? "" : null) };
      if (adDialog.editId) {
        await api.put(`/ads/${adDialog.editId}`, body);
        toast.success("Ad updated");
      } else {
        await api.post("/ads", { campaign_id: adDialog.campaign_id, adset_id: adDialog.adset_id, ...body });
        toast.success("Ad created");
      }
      setExpanded((p) => ({ ...p, [adDialog.campaign_id]: true }));
      setAdDialog({ open: false, campaign_id: null, adset_id: null, editId: null });
      load();
    } catch (e) { toast.error("Failed to save ad"); }
    finally { setSaving(false); }
  };
  const deleteAd = async (id) => {
    await api.delete(`/ads/${id}`);
    toast.success("Ad deleted");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-2 text-xs text-zinc-500 font-mono bg-[#14181B] border border-[#242B30] rounded-lg px-3 py-2 max-w-2xl">
          <Info className="h-4 w-4 text-lime-400 shrink-0 mt-0.5" />
          <span>Live Meta campaigns sync in automatically and are marked <span className="text-lime-400 font-mono">live</span> — status and budget edits are pushed back to Meta. Anything you create here stays a local <span className="font-mono">draft</span>.</span>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-campaign-btn" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-5 min-h-[46px] text-sm font-semibold text-[#0A0D0E] transition-colors">
              <Plus className="h-4 w-4" /> New Campaign
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#14181B] border-[#242B30] text-white max-w-lg w-[calc(100vw-2rem)] sm:w-full max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">New Draft Campaign</DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">Plan a campaign as a draft. Nothing is published to Meta.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Name</Label>
                <Input data-testid="campaign-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono text-sm" />
                <div className="mt-1.5"><NamingBadge name={form.name} /></div>
                <p className="mt-1 text-[10px] text-zinc-600 font-mono">e.g. KARYOW_AWR_GLOBAL_2026Q4</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Objective</Label>
                  <Select value={form.objective} onValueChange={(v) => setForm({ ...form, objective: v })}>
                    <SelectTrigger data-testid="campaign-objective" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                      {OBJECTIVES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Market</Label>
                  <Select value={form.market} onValueChange={(v) => setForm({ ...form, market: v })}>
                    <SelectTrigger data-testid="campaign-market" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                      {MARKETS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Daily Budget ({cur})</Label>
                  <Input data-testid="campaign-budget" type="number" value={form.daily_budget} onChange={(e) => setForm({ ...form, daily_budget: e.target.value })}
                    className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Start Date</Label>
                  <Input data-testid="campaign-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono [color-scheme:dark]" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Audience Notes</Label>
                <Textarea data-testid="campaign-audience" value={form.audience_notes} onChange={(e) => setForm({ ...form, audience_notes: e.target.value })}
                  className="mt-1.5 bg-[#0F1315] border-[#242B30] text-sm" placeholder="Targeting, interests, exclusions…" />
              </div>
            </div>
            <DialogFooter>
              <button data-testid="save-campaign-btn" onClick={create} disabled={saving}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-5 min-h-[46px] text-sm font-semibold text-[#0A0D0E] disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Draft
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 text-lime-400 animate-spin" /></div>
      ) : data.campaigns.length === 0 ? (
        <div className="text-center py-32 bg-[#14181B] border border-[#242B30] rounded-xl text-zinc-400">No campaigns yet.</div>
      ) : (
        <div className="space-y-3">
          {data.campaigns.map((c) => {
            const sets = data.adsets.filter((a) => a.campaign_id === c.id);
            const isOpen = expanded[c.id];
            return (
              <div key={c.id} data-testid={`campaign-row-${c.id}`} className="bg-[#14181B] border border-[#242B30] rounded-xl overflow-hidden">
                <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-4">
                  <button onClick={() => toggle(c.id)} className="flex items-center gap-2 min-w-0 flex-1 text-left" data-testid={`expand-${c.id}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-white truncate max-w-[70vw] sm:max-w-none">{c.name}</span>
                        <NamingBadge name={c.name} />
                        {c.data_source === "meta" && <span className="text-[9px] font-mono uppercase text-lime-400 border border-lime-400/30 bg-lime-400/10 rounded px-1">live</span>}
                        {c.data_source === "draft" && <span className="text-[9px] font-mono uppercase text-zinc-500 border border-[#242B30] rounded px-1">draft</span>}
                      </div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{c.objective} · {c.market} · {c.adset_count} ad sets · {c.ad_count} ads</div>
                    </div>
                  </button>

                  <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                    <div className="text-left sm:text-right">
                      <div className="font-mono text-sm text-white">{fmtMoney(c.metrics?.spend || 0, cur)}</div>
                      <div className="text-[10px] text-zinc-500 font-mono uppercase">spend</div>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="font-mono text-sm text-white">{fmtPct(c.metrics?.ctr || 0)}</div>
                      <div className="text-[10px] text-zinc-500 font-mono uppercase">ctr</div>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="font-mono text-sm text-white">{fmtMoney(c.metrics?.cpc || 0, cur)}</div>
                      <div className="text-[10px] text-zinc-500 font-mono uppercase">cpc</div>
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto sm:ml-0">
                      <Select value={c.status} onValueChange={(v) => updateStatus(c.id, v)}>
                        <SelectTrigger data-testid={`status-${c.id}`} className={`w-[104px] sm:w-32 h-10 min-h-[40px] text-xs font-mono border ${statusStyle(c.status)}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button data-testid={`delete-${c.id}`} onClick={() => remove(c.id)} className="p-2.5 -mr-1 text-zinc-600 hover:text-rose-400 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-[#242B30] bg-[#0F1315] p-4 space-y-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Ad Sets ({sets.length})</span>
                      <button data-testid={`add-adset-${c.id}`} onClick={() => openAdSet(c.id)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-lime-400/40 text-lime-400 hover:bg-lime-400/10 px-3 py-1.5 text-xs font-semibold transition-colors">
                        <Plus className="h-3.5 w-3.5" /> Add Ad Set
                      </button>
                    </div>
                    {sets.length === 0 && <p className="text-xs text-zinc-500 font-mono">No ad sets yet. Create one to organize your ads.</p>}
                    {sets.map((s) => {
                      const setAds = data.ads.filter((a) => a.adset_id === s.id);
                      return (
                        <div key={s.id} className="rounded-lg border border-[#242B30] p-3">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap min-w-0">
                              <span className="font-mono text-xs text-cyan-400 truncate max-w-[60vw] sm:max-w-none">{s.name}</span>
                              <NamingBadge name={s.name} re={AS_RE} />
                            </div>
                            <div className="flex items-center gap-1">
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${statusStyle(s.status)}`}>{s.status}</span>
                              <button data-testid={`edit-adset-${s.id}`} onClick={() => openEditAdSet(s)} className="p-2 text-zinc-600 hover:text-lime-400"><Pencil className="h-4 w-4" /></button>
                              <button data-testid={`delete-adset-${s.id}`} onClick={() => deleteAdSet(s.id)} className="p-2 -mr-1 text-zinc-600 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {setAds.map((a) => (
                              <span key={a.id} className="group inline-flex max-w-full items-center gap-1.5 text-[11px] font-mono text-zinc-400 bg-[#14181B] border border-[#242B30] rounded px-2 py-1.5">
                                <span className="truncate max-w-[45vw] sm:max-w-none">{a.name}</span>
                                <span className="text-zinc-600 hidden sm:inline">· {a.format}</span>
                                {a.creative_id && (
                                  <span className="text-lime-400" title={creativeById(a.creative_id)?.title || "Linked to AI creative"}>◆</span>
                                )}
                                <button data-testid={`edit-ad-${a.id}`} onClick={() => openEditAd(a)} className="p-1.5 text-zinc-600 hover:text-lime-400"><Pencil className="h-3.5 w-3.5" /></button>
                                <button data-testid={`delete-ad-${a.id}`} onClick={() => deleteAd(a.id)} className="p-1.5 text-zinc-600 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                              </span>
                            ))}
                            {setAds.length === 0 && <span className="text-[11px] text-zinc-600 font-mono">No ads</span>}
                            <button data-testid={`add-ad-${s.id}`} onClick={() => openAd(c.id, s.id)}
                              className="inline-flex items-center gap-1 rounded border border-dashed border-[#323D44] text-zinc-400 hover:text-lime-400 hover:border-lime-400/40 px-2 py-1 text-[11px] font-mono transition-colors">
                              <Plus className="h-3 w-3" /> Add Ad
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Ad Set dialog */}
      <Dialog open={asDialog.open} onOpenChange={(v) => setAsDialog({ ...asDialog, open: v })}>
        <DialogContent className="bg-[#14181B] border-[#242B30] text-white max-w-md w-[calc(100vw-2rem)] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{asDialog.editId ? "Edit Ad Set" : "New Ad Set"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Group ads by audience & budget. Draft only — nothing is published to Meta.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Name</Label>
              <Input data-testid="adset-name-input" value={asForm.name} onChange={(e) => setAsForm({ ...asForm, name: e.target.value })}
                className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono text-sm" />
              <div className="mt-1.5"><NamingBadge name={asForm.name} re={AS_RE} /></div>
              <p className="mt-1 text-[10px] text-zinc-600 font-mono">e.g. AS_BROAD_MARKET01_18PLUS</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Daily Budget ({cur})</Label>
                <Input data-testid="adset-budget" type="number" value={asForm.daily_budget} onChange={(e) => setAsForm({ ...asForm, daily_budget: e.target.value })}
                  className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Status</Label>
                <Select value={asForm.status} onValueChange={(v) => setAsForm({ ...asForm, status: v })}>
                  <SelectTrigger data-testid="adset-status" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Audience Notes</Label>
              <Textarea data-testid="adset-audience" value={asForm.audience_notes} onChange={(e) => setAsForm({ ...asForm, audience_notes: e.target.value })}
                className="mt-1.5 bg-[#0F1315] border-[#242B30] text-sm" placeholder="Targeting, placements, exclusions…" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="save-adset-btn" onClick={createAdSet} disabled={saving}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-5 min-h-[46px] text-sm font-semibold text-[#0A0D0E] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {asDialog.editId ? "Save Changes" : "Create Ad Set"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Ad dialog */}
      <Dialog open={adDialog.open} onOpenChange={(v) => setAdDialog({ ...adDialog, open: v })}>
        <DialogContent className="bg-[#14181B] border-[#242B30] text-white max-w-md w-[calc(100vw-2rem)] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{adDialog.editId ? "Edit Ad" : "New Ad"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Add a creative to this ad set. Draft only — nothing is published to Meta.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Name</Label>
              <Input data-testid="ad-name-input" value={adForm.name} onChange={(e) => setAdForm({ ...adForm, name: e.target.value })}
                className="mt-1.5 bg-[#0F1315] border-[#242B30] font-mono text-sm" />
              <div className="mt-1.5"><NamingBadge name={adForm.name} re={AD_RE} /></div>
              <p className="mt-1 text-[10px] text-zinc-600 font-mono">e.g. AD01_GLOBAL_MARKETPLACE_VIDEO</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Format</Label>
                <Select value={adForm.format} onValueChange={(v) => setAdForm({ ...adForm, format: v })}>
                  <SelectTrigger data-testid="ad-format" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                    {FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">Status</Label>
                <Select value={adForm.status} onValueChange={(v) => setAdForm({ ...adForm, status: v })}>
                  <SelectTrigger data-testid="ad-status" className="mt-1.5 bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 font-mono uppercase tracking-wider">AI Creative</Label>
              <Select value={adForm.creative_id || "none"} onValueChange={attachCreative}>
                <SelectTrigger data-testid="ad-creative" className="mt-1.5 bg-[#0F1315] border-[#242B30]">
                  <SelectValue placeholder="No creative attached" />
                </SelectTrigger>
                <SelectContent className="bg-[#14181B] border-[#242B30] text-white max-h-64">
                  <SelectItem value="none">No creative attached</SelectItem>
                  {creatives.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.title || c.service} · {c.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {adForm.creative_id ? (
                <div data-testid="ad-creative-preview" className="mt-2 rounded-lg border border-lime-400/25 bg-lime-400/5 p-2.5">
                  <p className="text-[11px] font-mono text-lime-400 truncate">
                    {(creativeById(adForm.creative_id)?.content?.headlines || [])[0] || creativeById(adForm.creative_id)?.title}
                  </p>
                  <p className="mt-1 text-[11px] text-zinc-400 line-clamp-2">
                    {(creativeById(adForm.creative_id)?.content?.primary_texts || [])[0] || "Saved AI creative"}
                  </p>
                </div>
              ) : (
                <p className="mt-1 text-[10px] text-zinc-600 font-mono">
                  {creatives.length === 0 ? "Save a creative in AI Ad Studio to link it here" : "Optional — links saved Studio copy to this ad"}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <button data-testid="save-ad-btn" onClick={createAd} disabled={saving}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-5 min-h-[46px] text-sm font-semibold text-[#0A0D0E] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {adDialog.editId ? "Save Changes" : "Create Ad"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
